import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedGradientText from '@/components/magicui/AnimatedGradientText';
import { Button } from '@/components/ui/button';
import { APP_STORE_LINK, GOOGLE_PLAY_LINK } from '../config/constants';
import { AppleIcon, GooglePlayIcon } from './StoreIcons';
import { submitN8nForm } from '@/services/n8nService';
import { PatientSupportForm, SuccessPanel, fieldInput, fieldLabel, primaryButton, textLink } from './PatientSupportForm';

const shareSubject = "Would you consider Quilt for our practice?";
// Sent as the email body and the WhatsApp text when a patient invites their clinic
const shareMessage = `Hi,

As a patient at your practice, I really appreciate the care I receive during my visits. I’m reaching out because I would love to use Quilt (https://quilt.gg), a generative AI platform that delivers personalized health and wellness guidance to help me stay on track with my health goals in between my appointments.

I’d love to use it to stay on track with my health goals under your guidance!

Quilt is designed to support both patients and providers:
- Better Continuous Care: It helps me follow through on my care plan and stay proactive about my health every day, not just when I’m in the office.
-Smarter Follow-Ups: It keeps me connected to your practice so I know when it’s time to schedule my regular check-ins and follow-up visits.
-Streamlined Communication: It makes it easier for your practice to send me tailored updates and outreach without adding extra work for your staff.

I would love the opportunity to use this tool as part of my care with you. You can learn more about how it works with medical practices here: https://quilt.gg/healthcare.

Would you consider taking a quick look at how it works?
Thanks!`;

const AppStoreButtons = () => (
    <div className="flex sm:flex-row flex-col items-center gap-4 mt-8 w-full sm:w-auto">
        <AnimatedGradientText className="w-full sm:w-auto p-0 rounded-2xl cursor-pointer bg-white hover:bg-slate-50 border border-slate-200 dark:border-none dark:bg-zinc-950 dark:hover:bg-zinc-900 group transition-all hover:scale-105">
            <a
                href={APP_STORE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-3 text-slate-900 dark:text-white px-6 py-4"
            >
                <AppleIcon height={32} />
                <div className="flex flex-col items-start">
                    <span className="text-xs text-slate-500 dark:text-gray-400">Download on the</span>
                    <span className="text-lg font-semibold leading-tight">App Store</span>
                </div>
            </a>
        </AnimatedGradientText>

        <AnimatedGradientText className="w-full sm:w-auto p-0 rounded-2xl cursor-pointer bg-white hover:bg-slate-50 border border-slate-200 dark:border-none dark:bg-zinc-950 dark:hover:bg-zinc-900 group transition-all hover:scale-105">
            <a
                href={GOOGLE_PLAY_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex w-full items-center justify-center gap-3 text-slate-900 dark:text-white px-6 py-4"
            >
                <GooglePlayIcon height={32} />
                <div className="flex flex-col items-start">
                    <span className="text-xs text-slate-500 dark:text-gray-400">Download on the</span>
                    <span className="text-lg font-semibold leading-tight">Play Store</span>
                </div>
            </a>
        </AnimatedGradientText>
    </div>
);

/** Eyebrow + heading + optional line of text, shared by every step so they read the same. */
const StepHeader = ({ eyebrow, title, subtitle }: { eyebrow: string; title: string; subtitle?: string }) => (
    <div className="w-full text-center">
        <p className="mb-3 text-sm font-medium uppercase tracking-widest text-purple-500">{eyebrow}</p>
        <h3 className="text-3xl font-light leading-tight text-slate-900 dark:text-white lg:text-4xl">{title}</h3>
        {subtitle && <p className="mx-auto mt-3 max-w-md font-light leading-relaxed text-slate-600 dark:text-zinc-400">{subtitle}</p>}
    </div>
);

const StepPanel = ({ children }: { children: React.ReactNode }) => (
    <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.25, ease: "easeInOut" }}
        className="mx-auto flex w-full max-w-md flex-col items-center gap-6"
    >
        {children}
    </motion.div>
);

/** Big tappable answer card for the either/or questions. */
const ChoiceCard = ({ title, description, onClick }: { title: string; description: string; onClick: () => void }) => (
    <button
        onClick={onClick}
        className="group flex w-full items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-5 text-left transition-all hover:-translate-y-0.5 hover:border-purple-300 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-950 dark:hover:border-purple-700"
    >
        <span>
            <span className="block text-lg font-medium text-slate-900 dark:text-white">{title}</span>
            <span className="mt-1 block text-sm font-light leading-relaxed text-slate-600 dark:text-zinc-400">{description}</span>
        </span>
        <svg className="h-5 w-5 shrink-0 text-slate-400 transition-colors group-hover:text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    </button>
);

export const PatientInteractiveCTA: React.FC = () => {
    const [step, setStep] = useState<number>(1);
    const [hasInvite, setHasInvite] = useState<boolean | null>(null);
    // Without an invite code: is the patient's clinic already working with Quilt? (null = not answered yet)
    const [clinicOnQuilt, setClinicOnQuilt] = useState<boolean | null>(null);

    // Reach Out Form State (clinic is not on Quilt yet)
    const [reachOutClinic, setReachOutClinic] = useState("");
    const [reachOutZip, setReachOutZip] = useState("");
    const [reachOutAddress, setReachOutAddress] = useState("");
    const [reachOutEmail, setReachOutEmail] = useState("");
    const [reachOutPhone, setReachOutPhone] = useState("");
    const [reachOutStatus, setReachOutStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [reachOutErrorMessage, setReachOutErrorMessage] = useState("");

    useEffect(() => {
        const handleOpenStep = (e: CustomEvent<{ step: number, hasInvite?: boolean | null }>) => {
            setStep(e.detail.step);
            setClinicOnQuilt(null);
            if (e.detail.hasInvite !== undefined) {
                setHasInvite(e.detail.hasInvite);
            }
        };

        window.addEventListener('openInteractiveCtaStep', handleOpenStep as EventListener);
        return () => window.removeEventListener('openInteractiveCtaStep', handleOpenStep as EventListener);
    }, []);

    const handleYes = () => {
        setHasInvite(true);
        setStep(2);
    };

    const handleNo = () => {
        setHasInvite(false);
        setClinicOnQuilt(null);
        setStep(2);
    };

    const handleResetToFirstStep = () => {
        setStep(1);
        setHasInvite(null);
        setClinicOnQuilt(null);
        setReachOutStatus('idle');
        setReachOutClinic("");
        setReachOutZip("");
        setReachOutAddress("");
        setReachOutEmail("");
        setReachOutPhone("");
    };

    const handleReachOutSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setReachOutStatus('loading');
        setReachOutErrorMessage("");

        try {
            const result = await submitN8nForm({
                type: 'lead',
                source: 'clinic_not_found',
                name: reachOutClinic.trim() || null,
                clinic_name: reachOutClinic.trim() || null,
                organization_name: reachOutClinic.trim() || null,
                email: reachOutEmail.trim() || null,
                phone: reachOutPhone.trim() || null,
                address: reachOutAddress.trim() || null,
                zip_code: reachOutZip.trim() || null,
                topic: 'Clinic Not Found',
                message: 'Patient submitted unlisted clinic for Quilt outreach',
            });

            if (result.success) {
                setReachOutStatus('success');
                setReachOutClinic("");
                setReachOutZip("");
                setReachOutAddress("");
                setReachOutEmail("");
                setReachOutPhone("");
            } else {
                setReachOutStatus('error');
                setReachOutErrorMessage(result.error || "Failed to submit. Try again.");
                setTimeout(() => setReachOutStatus('idle'), 4000);
            }
        } catch (error: any) {
            console.error("Clinic reach out submission error:", error);
            setReachOutStatus('error');
            setReachOutErrorMessage("Failed to submit. Try again.");
            setTimeout(() => setReachOutStatus('idle'), 4000);
        }
    };

    const contentRef = useRef<HTMLDivElement>(null);
    const [measuredHeight, setMeasuredHeight] = useState<number | undefined>(undefined);

    useEffect(() => {
        const el = contentRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => {
            setMeasuredHeight(el.scrollHeight);
        });
        ro.observe(el);
        setMeasuredHeight(el.scrollHeight);

        return () => ro.disconnect();
    }, []);

    return (
        <div className="relative mx-auto my-0 w-full max-w-2xl rounded-3xl border border-purple-200/70 bg-gradient-to-br from-white via-purple-50/60 to-white p-6 text-center shadow-xl shadow-purple-100/50 sm:p-10 md:my-16 lg:p-12 dark:border-purple-500/20 dark:from-zinc-900 dark:via-purple-950/20 dark:to-zinc-900 dark:shadow-none">
            <motion.div
                animate={{ height: measuredHeight }}
                transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
                <div ref={contentRef}>
                    <AnimatePresence mode="wait">

                        {step === 1 && (
                            <StepPanel key="step-1">
                                <StepHeader eyebrow="Get started" title="Do you have an invite code?" subtitle="You'll need one from your clinic to create your Quilt account." />
                                <div className="flex w-full flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
                                    <Button onClick={handleYes} size="lg" className={`w-full sm:w-40 ${primaryButton}`}>
                                        Yes
                                    </Button>
                                    <Button onClick={handleNo} size="lg" variant="outline" className="h-12 w-full rounded-full border-slate-300 px-8 text-base sm:w-40 dark:border-zinc-700">
                                        No
                                    </Button>
                                </div>
                            </StepPanel>
                        )}

                        {step === 2 && hasInvite && (
                            <StepPanel key="step-2-yes">
                                <StepHeader eyebrow="You're all set" title="Download the app to get started" subtitle="Open it and enter your email and invite code." />
                                <AppStoreButtons />
                                <button onClick={() => setStep(1)} className={textLink}>Go back</button>
                            </StepPanel>
                        )}

                        {step === 2 && !hasInvite && clinicOnQuilt === null && (
                            <StepPanel key="step-2-clinic">
                                <StepHeader eyebrow="Support" title="Is your clinic working with Quilt?" subtitle="We'll point you to the right place." />
                                <div className="flex w-full flex-col gap-3">
                                    <ChoiceCard
                                        title="Yes, my clinic uses Quilt"
                                        description="Ask us for help getting your invite code."
                                        onClick={() => setClinicOnQuilt(true)}
                                    />
                                    <ChoiceCard
                                        title="No, or I'm not sure"
                                        description="We'll reach out to your clinic and invite them to join."
                                        onClick={() => setClinicOnQuilt(false)}
                                    />
                                </div>
                                <button onClick={() => setStep(1)} className={textLink}>Go back</button>
                            </StepPanel>
                        )}

                        {step === 2 && !hasInvite && clinicOnQuilt === true && (
                            <StepPanel key="step-2-support">
                                <PatientSupportForm
                                    header={<StepHeader eyebrow="Support" title="Contact support" subtitle="Fill out the form and our team will help you get started." />}
                                    onBack={() => setClinicOnQuilt(null)}
                                    successActionLabel="Back to start"
                                    onSuccessAction={handleResetToFirstStep}
                                />
                            </StepPanel>
                        )}

                        {step === 2 && !hasInvite && clinicOnQuilt === false && (
                            <StepPanel key="step-2-reachout">
                                {reachOutStatus === 'success' ? (
                                    <SuccessPanel title="Request received!" message="Thanks! We'll reach out to your clinic." actionLabel="Back to start" onAction={handleResetToFirstStep} />
                                ) : (
                                    <>
                                        <StepHeader eyebrow="Invite your clinic" title="Bring Quilt to your clinic" subtitle="Tell us how to reach them and we'll take it from here." />
                                        <form onSubmit={handleReachOutSubmit} className="w-full space-y-4 text-left">
                                            <div>
                                                <label htmlFor="reachout_clinic" className={fieldLabel}>Clinic name *</label>
                                                <input
                                                    id="reachout_clinic"
                                                    name="clinic_name"
                                                    type="text"
                                                    autoComplete="organization"
                                                    value={reachOutClinic}
                                                    onChange={(e) => setReachOutClinic(e.target.value)}
                                                    placeholder="Name of your clinic or practice"
                                                    className={fieldInput}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="reachout_email" className={fieldLabel}>Clinic email *</label>
                                                <input
                                                    id="reachout_email"
                                                    name="email"
                                                    type="email"
                                                    autoComplete="email"
                                                    value={reachOutEmail}
                                                    onChange={(e) => setReachOutEmail(e.target.value)}
                                                    placeholder="clinic@example.com"
                                                    className={fieldInput}
                                                    required
                                                />
                                            </div>
                                            <div>
                                                <label htmlFor="reachout_phone" className={fieldLabel}>Phone (Optional)</label>
                                                <input
                                                    id="reachout_phone"
                                                    name="phone"
                                                    type="tel"
                                                    inputMode="tel"
                                                    autoComplete="tel"
                                                    value={reachOutPhone}
                                                    onChange={(e) => setReachOutPhone(e.target.value)}
                                                    placeholder="(555) 000-0000"
                                                    className={fieldInput}
                                                />
                                            </div>
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                                                <div className="sm:col-span-2">
                                                    <label htmlFor="reachout_address" className={fieldLabel}>Address (Optional)</label>
                                                    <input
                                                        id="reachout_address"
                                                        name="address"
                                                        type="text"
                                                        autoComplete="street-address"
                                                        value={reachOutAddress}
                                                        onChange={(e) => setReachOutAddress(e.target.value)}
                                                        placeholder="123 Medical Center Dr"
                                                        className={fieldInput}
                                                    />
                                                </div>
                                                <div>
                                                    <label htmlFor="reachout_zip" className={fieldLabel}>Zip code (Optional)</label>
                                                    <input
                                                        id="reachout_zip"
                                                        name="zipcode"
                                                        type="text"
                                                        inputMode="numeric"
                                                        autoComplete="postal-code"
                                                        value={reachOutZip}
                                                        onChange={(e) => setReachOutZip(e.target.value)}
                                                        placeholder="78701"
                                                        className={fieldInput}
                                                    />
                                                </div>
                                            </div>

                                            <Button type="submit" disabled={reachOutStatus === 'loading'} className={`w-full ${primaryButton} mt-2`}>
                                                {reachOutStatus === 'loading' ? 'Submitting...' : 'Submit to Quilt'}
                                            </Button>

                                            {reachOutStatus === 'error' && <p className="text-center text-sm font-medium text-red-500">{reachOutErrorMessage || 'Failed to submit. Try again.'}</p>}
                                        </form>

                                        <div className="flex w-full items-center gap-3">
                                            <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
                                            <span className="text-xs font-medium uppercase tracking-widest text-slate-400">or</span>
                                            <div className="h-px flex-1 bg-slate-200 dark:bg-zinc-800" />
                                        </div>

                                        <div className="w-full space-y-3 text-center">
                                            <p className="font-light text-slate-600 dark:text-zinc-400">Invite them yourself</p>
                                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                                <a
                                                    href={`mailto:?subject=${encodeURIComponent(shareSubject)}&body=${encodeURIComponent(shareMessage)}`}
                                                    className="flex h-12 items-center justify-center gap-2 rounded-full border border-slate-300 bg-white text-base font-medium text-slate-800 transition-colors hover:bg-slate-100 dark:border-zinc-700 dark:bg-zinc-950 dark:text-zinc-200 dark:hover:bg-zinc-900"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="20" height="16" x="2" y="4" rx="2" /><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" /></svg>
                                                    Email
                                                </a>
                                                <a
                                                    href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="flex h-12 items-center justify-center gap-2 rounded-full bg-[#25D366] text-base font-medium text-white transition-colors hover:bg-[#128C7E]"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>
                                                    WhatsApp
                                                </a>
                                            </div>
                                        </div>
                                        <button onClick={() => setClinicOnQuilt(null)} className={textLink}>Go back</button>
                                    </>
                                )}
                            </StepPanel>
                        )}

                    </AnimatePresence>
                </div>
            </motion.div>
        </div>
    );
};
